import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { useSelector } from 'react-redux';
import SearchContext from '../../templates/Search/SearchContext';
import {
  useMediaQueryBreakPoint,
  IS_PHONE_OR_TABLET,
} from '../../../src/hooks';
import { ListMode } from '../SearchResults/types';
import { isDemoAccountSelector } from '@/src/selectors';

interface Props {
  mode?: ListMode;
  item?: CardHcd & { markerRef?: React.RefObject<HTMLElement> };
  onClick: React.ReactEventHandler;
  children: ReactNode;
  className?: string;
  waitingVideoSessions: Array<any>;
  withTheSamePractitioner: boolean;
}

const SearchCardWrapper = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      mode,
      item,
      onClick,
      children,
      className,
      waitingVideoSessions,
      withTheSamePractitioner,
    },
    ref,
  ) => {
    const ctx = React.useContext(SearchContext);
    const isDemoAccount = useSelector(isDemoAccountSelector);
    const hasCtx: boolean = !!ctx;
    const timeoutId = React.useRef<NodeJS.Timeout | undefined>();
    const hasWaitingVideoSession: boolean = waitingVideoSessions?.length > 0;
    let couldDoTLC: boolean =
      !hasWaitingVideoSession || withTheSamePractitioner;
    if (isDemoAccount && couldDoTLC)
      couldDoTLC = item?.settings?.isDemo === true;
    const isPhoneOrTablet: boolean = useMediaQueryBreakPoint(
      IS_PHONE_OR_TABLET,
    );

    const onMouseEnter = React.useCallback(() => {
      if (!hasCtx) return;
      ctx.setHighlightItemId(item?.id);
      const fly = async () => {
        const FlyToInterpolator = (
          await import(
            'react-map-gl/dist/es6/utils/transition/viewport-fly-to-interpolator'
          )
        ).default;
        ctx.setMapViewport({
          longitude: item?.publicInformation?.address?.location?.x,
          latitude: item?.publicInformation?.address?.location?.y,
          zoom: 17,
          bearing: 0,
          pitch: 0,
          transitionDuration: 500,
          transitionInterpolator: new FlyToInterpolator(),
        });
      };
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      timeoutId.current = setTimeout(fly, 2000);
    }, [item]);

    const onMouseLeave = React.useCallback(() => {
      if (!hasCtx) return;
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
      }
      ctx.setMapViewport(ctx.mapViewportDefault);
      if (!(item?.markerRef && item.markerRef.current)) return;
      ctx.setHighlightItemId(null);
    }, [item, ctx?.mapViewportDefault]);

    const onClickEnabled = React.useMemo(
      () => isPhoneOrTablet || mode === 'upcomingTlc',
      [isPhoneOrTablet, mode],
    );

    return (
      <div
        className={clsx(className, 'search-card', {
          'search-card--tlc': mode,
          'search-card--tlc--clickable': onClickEnabled,
          disabled: mode && !couldDoTLC,
        })}
        ref={ref}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...(onClickEnabled ? { onClick } : {})}
        data-testid="search-card"
      >
        {children}
      </div>
    );
  },
);

SearchCardWrapper.displayName = 'SearchCardWrapper';

export default SearchCardWrapper;
